import parseISO from "date-fns/parseISO"
import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"
import { useQueryClient } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"
import PayInvoicesDialog from "src/main/Billing/PayInvoices/PayInvoicesDialog"
import SelectInvoicesDialog from "src/main/Billing/PayInvoices/SelectInvoicesDialog"
import { countedCopy } from "src/main/Billing/PayInvoices/copy"
import {
  payableContactInvoicesKey,
  usePayableContactInvoices,
} from "src/main/Billing/PayInvoices/hooks"
import { PAYMENT_PROCESSING } from "src/main/Billing/PayInvoices/methods"
import { opensInPlace, useBillingSheets } from "src/main/Billing/Sheets/context"

import DataTableV2, { navigateToUrl } from "src/components/DataTableV2"

import {
  fetchContactInvoicesForPrint,
  getContactInvoiceIds,
  getContactInvoices,
} from "src/api/Billing/Payments"

import useDatatable from "src/hooks/use_datatable"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import ContactBalances, { initialLedgerDataProps } from "./ContactBalances"
import ContactInvoicesActions from "./ContactInvoicesActions"
import ContactInvoicesFilters from "./ContactInvoicesFilters"
import ContactInvoicesPrintView from "./ContactInvoicesPrintView"
import InvoiceBalances, { ledgerQueryKey } from "./InvoiceBalances"
import { buildColumns, buildPayColumns } from "./columns"
import { calculateSummaryTotals, isPayable } from "./helpers"
import useInvoiceStatusPolling from "./useInvoiceStatusPolling"
import useSelectedInvoices from "./useSelectedInvoices"

const STATUS_LABELS = {
  past_due: "Overdue",
  failed: "Failed",
  settled: "Settled",
  processing: "Processing",
  disputed: "Disputed",
  scheduled: "Scheduled",
}

const ContactInvoicesPanel = ({
  contactId,
  initialLedgerData,
  contact,
  marina,
  payInvoicesDialog,
}) => {
  const tracker = useTracker()
  const queryClient = useQueryClient()
  const showToast = useToast()
  const { marinaSlug, presentmentCurrency } = useMarina()
  const [printData, setPrintData] = useState(null)
  const sheets = useBillingSheets()
  const [payDialog, setPayDialog] = useState(null)
  const [isSelectingInvoices, setIsSelectingInvoices] = useState(false)
  const {
    enabled: canPayInvoices,
    i18n: payCopy,
    tabI18n: tabCopy,
    maxInvoicesPerPayment,
    cardReaders,
    enableBluetoothCardReader,
  } = payInvoicesDialog
  const tab = canPayInvoices
    ? {
        tableName: "contact-invoices-pay",
        columns: buildPayColumns({ presentmentCurrency, copy: tabCopy }),
      }
    : {
        tableName: "contact-invoices",
        columns: buildColumns({ presentmentCurrency }),
      }

  const { data: payableInvoices, isSuccess: isPayableListLoaded } =
    usePayableContactInvoices({
      marinaSlug,
      contactId,
      enabled: canPayInvoices,
    })
  const hasOpenInvoices = payableInvoices?.length > 0

  const table = useDatatable({
    queryKey: ["contactInvoices", contactId],
    fetchFn: (params) =>
      getContactInvoices({ contactId, marinaSlug, ...params }),
    fetchAllIds: canPayInvoices
      ? (params) => getContactInvoiceIds({ contactId, marinaSlug, ...params })
      : undefined,
    defaultFilters: {
      statuses: [],
      due_date_lteq: "",
      due_date_gteq: "",
    },
  })

  const {
    invoices: selectedInvoices,
    isLoading: isSelectionLoading,
    isError: isSelectionError,
    refresh: refreshSelectedInvoices,
  } = useSelectedInvoices({
    marinaSlug,
    contactId,
    selection: table.selection,
    loadedRows: table.records,
    enabled: canPayInvoices,
  })

  useInvoiceStatusPolling({
    marinaSlug,
    contactId,
    invoices: table.records,
    enabled: canPayInvoices,
    onStatusChange: () => refreshTab(),
  })

  useEffect(() => {
    if (printData) window.print()
  }, [printData])

  const printInvoices = async (request, printDetails) => {
    const response = await fetchContactInvoicesForPrint({
      contactId,
      marinaSlug,
      ...request,
    })
    const invoices = response.data || []
    setPrintData({
      invoices,
      summaryTotals: calculateSummaryTotals(invoices),
      ...printDetails,
    })
  }

  const handleExportSummary = () => {
    tracker.trackEvent("customer_invoices_tab:export_summary_pressed")
    const { filters } = table
    printInvoices(
      {
        filters: {
          statuses: filters.statuses,
          due_date_gteq: filters.due_date_gteq,
          due_date_lteq: filters.due_date_lteq,
        },
      },
      {
        startDate: filters.due_date_gteq
          ? parseISO(filters.due_date_gteq)
          : null,
        endDate: filters.due_date_lteq ? parseISO(filters.due_date_lteq) : null,
        statusFilters: (filters.statuses || []).map(
          (s) => STATUS_LABELS[s] || s
        ),
      }
    )
  }

  const handleExportSelected = () => {
    tracker.trackEvent("customer_invoices_tab:export_selected_pressed")
    printInvoices(
      { ids: table.selection },
      {
        selectionText: countedCopy(
          payCopy.print.selectedInvoices,
          table.selection.length
        ),
      }
    )
  }

  const rowConfig = useMemo(() => {
    const receiptUrl = (invoice) =>
      `/manage/${marinaSlug}/payments/${invoice.id}/view`
    const trackViewClick = () =>
      tracker.trackEvent("customer_invoices_tab:view_invoice_pressed")

    return {
      getViewUrl: receiptUrl,
      onViewClick: trackViewClick,
      onClick: (invoice, event) => {
        if (invoice.detailPath && sheets && opensInPlace(event)) {
          sheets.openInvoice(invoice.id)
          return
        }

        if (!invoice.detailPath) trackViewClick()
        navigateToUrl(invoice.detailPath ?? receiptUrl(invoice), event)
      },
    }
  }, [marinaSlug, sheets, tracker])

  const actions = useMemo(() => {
    const openSource = (invoice) => {
      tracker.trackEvent("customer_invoices_tab:view_source_pressed")
      window.open(invoice.sourcePath, "_blank")
    }

    return [
      {
        label: "View Reservation",
        icon: "calendar-days",
        iconStyle: "solid",
        hidden: (invoice) => invoice.sourceType !== "Reservation",
        onClickSingle: openSource,
      },
      {
        label: "View Contract",
        icon: "file-contract text-sm",
        iconStyle: "solid",
        hidden: (invoice) => invoice.sourceType !== "Contract",
        onClickSingle: openSource,
      },
      {
        label: "View Sale",
        icon: "money-bills",
        iconStyle: "solid",
        hidden: (invoice) => invoice.sourceType !== "Sale",
        onClickSingle: openSource,
      },
    ]
  }, [tracker])

  const openPayDialog = (invoices, entryPoint) =>
    setPayDialog({ invoices, entryPoint })

  const payActions = canPayInvoices
    ? [
        {
          label: payCopy.entryPoints.payInvoice,
          icon: "hand-holding-dollar",
          iconStyle: "solid",
          hidden: (invoice) => !isPayable(invoice),
          onClickSingle: (invoice) => {
            table.setSelection([invoice.id])
            openPayDialog([invoice], "customer_invoices_row")
          },
        },
      ]
    : []

  const refreshTab = () => {
    queryClient.invalidateQueries(["contactInvoices", contactId])
    refreshSelectedInvoices()
    queryClient.invalidateQueries(
      payableContactInvoicesKey({ marinaSlug, contactId })
    )
    queryClient.invalidateQueries(
      ledgerQueryKey({ marinaSlug, ledgerId: initialLedgerData.id })
    )
  }

  const handlePayDialogClose = ({ needsRefresh }) => {
    setPayDialog(null)
    if (needsRefresh) {
      refreshTab()
    }
  }

  const handlePaid = (outcome) => {
    const invoiceCount = payDialog.invoices.length
    setPayDialog(null)
    table.setSelection([])
    refreshTab()
    showToast(
      countedCopy(
        outcome === PAYMENT_PROCESSING
          ? payCopy.outcome.invoicesProcessing
          : payCopy.outcome.invoicesPaid,
        invoiceCount
      ),
      { type: "success" }
    )
  }

  return (
    <>
      <div
        data-testid="contact-invoices-panel"
        className="flex flex-col space-y-4 px-4 print:hidden"
      >
        {canPayInvoices ? (
          <InvoiceBalances
            initialLedgerData={initialLedgerData}
            copy={tabCopy}
          />
        ) : (
          <ContactBalances initialLedgerData={initialLedgerData} />
        )}
        <ContactInvoicesFilters
          table={table}
          onExportSummary={handleExportSummary}
          actions={
            canPayInvoices ? (
              <ContactInvoicesActions
                selectionCount={table.selection.length}
                selectedInvoices={selectedInvoices}
                isSelectionLoading={isSelectionLoading}
                isSelectionError={isSelectionError}
                hasOpenInvoices={hasOpenInvoices}
                isPayableListLoaded={isPayableListLoaded}
                maxInvoicesPerPayment={maxInvoicesPerPayment}
                copy={payCopy}
                onExportAll={handleExportSummary}
                onExportSelected={handleExportSelected}
                onPayOpenInvoices={() => setIsSelectingInvoices(true)}
                onPaySelected={(openInvoices) =>
                  openPayDialog(openInvoices, "customer_invoices_bulk")
                }
              />
            ) : undefined
          }
        />
        <DataTableV2
          name={tab.tableName}
          columns={tab.columns}
          data={table.records}
          rowConfig={rowConfig}
          actions={[...actions, ...payActions]}
          selectable={canPayInvoices}
          maxHeight="calc(100vh - 150px)"
          {...table.tableProps}
        />
      </div>
      {printData ? (
        <ContactInvoicesPrintView
          invoices={printData.invoices}
          contact={contact}
          marina={marina}
          summaryTotals={printData.summaryTotals}
          presentmentCurrency={presentmentCurrency}
          startDate={printData.startDate}
          endDate={printData.endDate}
          statusFilters={printData.statusFilters}
          selectionText={printData.selectionText}
        />
      ) : null}
      {isSelectingInvoices && (
        <SelectInvoicesDialog
          contactId={contactId}
          copy={payCopy}
          maxInvoicesPerPayment={maxInvoicesPerPayment}
          onContinue={(invoices) => {
            setIsSelectingInvoices(false)
            openPayDialog(invoices, "customer_invoices_pay_open")
          }}
          onClose={() => setIsSelectingInvoices(false)}
        />
      )}
      {payDialog && (
        <PayInvoicesDialog
          invoices={payDialog.invoices}
          contact={contact}
          contactId={contactId}
          entryPoint={payDialog.entryPoint}
          copy={payCopy}
          cardReaders={cardReaders}
          enableBluetoothCardReader={enableBluetoothCardReader}
          onPaid={handlePaid}
          onClose={handlePayDialogClose}
        />
      )}
    </>
  )
}

ContactInvoicesPanel.propTypes = {
  contactId: PropTypes.string.isRequired,
  initialLedgerData: PropTypes.shape(initialLedgerDataProps).isRequired,
  contact: PropTypes.object.isRequired,
  marina: PropTypes.object.isRequired,
  payInvoicesDialog: PropTypes.shape({
    enabled: PropTypes.bool.isRequired,
    i18n: PropTypes.object,
    tabI18n: PropTypes.object,
    maxInvoicesPerPayment: PropTypes.number,
    cardReaders: PropTypes.array,
    enableBluetoothCardReader: PropTypes.bool,
  }).isRequired,
}

export default ContactInvoicesPanel
